// import NetInfo, {
//   addEventListener,
//   useNetInfo,
// } from "@react-native-community/netinfo";
import { enableNetwork, disableNetwork } from "firebase/firestore";
import db, { app, remoteConfig } from "../Firestore/FirestoreConfig";
import { makeAutoObservable, runInAction } from "mobx";
import { fetchAndActivate, getValue } from "firebase/remote-config";
import { SyncStatus } from "../../Resources/Constants";

const FUNCTIONS_REGION = "europe-west1";
const TIME_ENDPOINT_TIMEOUT_MS = 5000;
const REMOTE_CONFIG_REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

// Time sync intervals - using Date.now() for reliability across sleep/hibernate
const PERIODIC_SYNC_INTERVAL_MS = 45 * 60 * 1000; // 45 minutes - periodic sync while tab is visible
const MAX_TRUSTED_SYNC_AGE_MS = 60 * 60 * 1000; // 1 hour - max age before forcing resync on visibility

// Connectivity check intervals - optimized for lightweight operation
const CONNECTIVITY_CHECK_TIMEOUT_MS = 3000; // 3 seconds timeout for connectivity checks (reduced from 5s)
const CONNECTIVITY_CHECK_INTERVAL_OFFLINE_MS = 60 * 1000; // Check every 60 seconds when offline (to detect when back online)
const CONNECTIVITY_CHECK_INTERVAL_ONLINE_MS = 5 * 60 * 1000; // Check every 5 minutes when online (fallback only - browser events are primary)
const CONNECTIVITY_VERIFY_DELAY_MS = 1500; // Wait 1.5 seconds before verifying after online event
const CONNECTIVITY_CHECK_MIN_INTERVAL_MS = 5 * 1000; // Minimum 5 seconds between checks (debouncing)
const CONNECTIVITY_RESULT_CACHE_MS = 10 * 1000; // Cache successful connectivity results for 10 seconds
const FIRESTORE_NETWORK_TOGGLE_MIN_INTERVAL_MS = 5000; // Min time between enable/disable transitions
const FIRESTORE_TOGGLE_RETRY_MAX_BACKOFF_MS = 5 * 60 * 1000; // Cap retries at 5 minutes
const FIRESTORE_TOGGLE_ERROR_LOG_WINDOW_MS = 10 * 60 * 1000; // Log at most once every 10 minutes

const RC_USE_SERVER_TIME = "use_server_time";

class NetworkManager {
  // Singleton instance
  static instance = null;

  connectionListener = null;
  beforeUnloadListener = null;
  visibilityChangeListener = null;
  remoteConfigRefreshIntervalId = null;
  remoteConfigRefreshing = false;
  connectivityCheckIntervalId = null;
  connectivityCheckInProgress = false;
  lastConnectivityCheck = null;
  lastConnectivityResult = null; // Cache last result to avoid redundant checks
  lastConnectivityResultTime = null;
  connectivityVerified = false; // Indicates connectivity confirmed via network request
  networkStateChangeInProgress = false; // Prevent concurrent enableNetwork/disableNetwork calls
  firestoreNetworkEnabled = true; // Firestore starts enabled by default on app init
  pendingFirestoreNetworkEnabled = null; // Latest desired Firestore network state (true/false)
  pendingFirestoreNetworkReason = null;
  firestoreNetworkChangePromise = null;
  firestoreNetworkReconciliationTimeoutId = null;
  pendingFirestoreReconciliationReason = null;
  firestoreToggleConsecutiveFailures = 0;
  firestoreToggleBackoffUntil = null;
  firestoreToggleErrorLastLoggedAt = null;
  firestoreToggleErrorSuppressedCount = 0;
  lastFirestoreNetworkChangeAt = null;
  connectivityEventVersion = 0; // Monotonic token to ignore stale async online/offline handlers

  isOffline = !window.navigator.onLine;

  constructor() {
    if (NetworkManager.instance) {
      return NetworkManager.instance;
    }
    NetworkManager.instance = this;

    this.projectId = app?.options?.projectId || null;

    // Remote-config flag: allow safe rollout / quick disable.
    // Defaults to false (local time) unless explicitly enabled.
    this.useAuthoritativeServerTime = false;
    this.remoteConfigLoaded = false;

    // Authoritative server time baseline (UTC epoch millis) and wall-clock baseline
    this.syncedServerTimeMs = null;
    this.syncedDateNowMs = null; // Wall-clock timestamp at sync time (replaces performance.now baseline)
    this.syncing = false;
    this.lastSyncSuccess = false;
    this.lastSyncError = null;
    this.syncAttempts = 0;

    // Periodic sync interval ID
    this.periodicSyncIntervalId = null;

    makeAutoObservable(this);

    // Load Remote Config and (optionally) enable authoritative time.
    this.loadRemoteConfigForTime();

    // Keep Remote Config and time mode up-to-date during the session.
    this.setupVisibilityChangeListener();
    this.setupRemoteConfigRefreshInterval();
    this.setupPeriodicSyncInterval();

    // CRITICAL: Initialize state based on Chrome's navigator.onLine
    // Then verify with network request to catch Chrome's false offline detection
    // Use small delay to avoid blocking initialization, but do it early
    setTimeout(() => {
      // Start with Chrome's reported state, then verify
      const chromeSaysOffline = !window.navigator.onLine;
      runInAction(() => {
        this.isOffline = chromeSaysOffline;
      });
      // Verify with network request to catch false positives
      this.verifyConnectivity(true); // Force check to fix isOffline if Chrome is wrong
    }, 100);
  }

  applyAuthoritativeServerTimeEnabled(enabled) {
    const wasEnabled = this.useAuthoritativeServerTime;

    runInAction(() => {
      this.useAuthoritativeServerTime = enabled;
    });

    if (enabled && !wasEnabled) {
      console.log(
        "🔄 NetworkManager: Authoritative server time ENABLED (Remote Config)",
      );
      console.log("🔄 NetworkManager: Initial time sync triggered");
      this.syncNetworkTime();
      return;
    }

    if (!enabled && wasEnabled) {
      console.log(
        "⏭️ NetworkManager: Authoritative server time DISABLED (Remote Config)",
      );
      runInAction(() => {
        this.syncedServerTimeMs = null;
        this.syncedDateNowMs = null;
        this.lastSyncSuccess = false;
        this.lastSyncError = null;
        this.syncAttempts = 0;
      });
    }
  }

  async refreshRemoteConfigForTime() {
    if (!remoteConfig) {
      runInAction(() => {
        this.remoteConfigLoaded = true;
      });
      this.applyAuthoritativeServerTimeEnabled(false);
      return;
    }

    if (this.remoteConfigRefreshing) {
      return;
    }

    this.remoteConfigRefreshing = true;

    try {
      try {
        await fetchAndActivate(remoteConfig);
      } catch (e) {
        // Keep last known value if fetch fails
      }

      let enabled = false;
      try {
        enabled = getValue(remoteConfig, RC_USE_SERVER_TIME).asBoolean();
      } catch (e) {
        enabled = false;
      }

      runInAction(() => {
        this.remoteConfigLoaded = true;
      });

      this.applyAuthoritativeServerTimeEnabled(enabled);
    } finally {
      this.remoteConfigRefreshing = false;
    }
  }

  async loadRemoteConfigForTime() {
    if (!remoteConfig) {
      runInAction(() => {
        this.remoteConfigLoaded = true;
      });
      this.applyAuthoritativeServerTimeEnabled(false);
      return;
    }

    await this.refreshRemoteConfigForTime();
  }

  setupRemoteConfigRefreshInterval() {
    if (this.remoteConfigRefreshIntervalId || !remoteConfig) {
      return;
    }

    this.remoteConfigRefreshIntervalId = setInterval(() => {
      if (this.isOffline) {
        return;
      }
      this.refreshRemoteConfigForTime();
    }, REMOTE_CONFIG_REFRESH_INTERVAL_MS);
  }

  /**
   * Sets up periodic time sync interval (every 45 minutes while tab is visible).
   * This ensures time stays accurate even during long sessions and handles
   * cases where the tab was hidden during sleep/hibernate.
   */
  setupPeriodicSyncInterval() {
    if (this.periodicSyncIntervalId) {
      return;
    }

    this.periodicSyncIntervalId = setInterval(() => {
      // Only sync if: online, not syncing, tab visible, server time enabled
      if (
        this.isOffline ||
        this.syncing ||
        !this.useAuthoritativeServerTime ||
        document.visibilityState !== "visible"
      ) {
        return;
      }

      // Check if sync is needed based on age
      const ageMs = this.syncedDateNowMs
        ? Date.now() - this.syncedDateNowMs
        : Infinity;

      if (ageMs >= PERIODIC_SYNC_INTERVAL_MS || ageMs < 0) {
        console.log("🔄 NetworkManager: Periodic sync triggered");
        this.syncNetworkTime();
      }
    }, PERIODIC_SYNC_INTERVAL_MS);
  }

  static getInstance() {
    if (!NetworkManager.instance) {
      NetworkManager.instance = new NetworkManager();
    }
    return NetworkManager.instance;
  }

  buildServerTimeUrl() {
    if (!this.projectId) {
      return null;
    }

    const isLocalhost =
      typeof window !== "undefined" &&
      (window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1");

    // IMPORTANT:
    // Do NOT automatically use the emulator just because the web app is running on localhost.
    // Most dev sessions do not run the Functions emulator, and this would make time sync fail.
    // Opt-in only (debug):
    // - add `?useFunctionsEmulator=1` to the URL, OR
    // - set `localStorage.setItem('useFunctionsEmulator', 'true')`
    const shouldUseFunctionsEmulator =
      isLocalhost &&
      typeof window !== "undefined" &&
      (new URLSearchParams(window.location.search).get(
        "useFunctionsEmulator",
      ) === "1" ||
        new URLSearchParams(window.location.search).get(
          "useFunctionsEmulator",
        ) === "true" ||
        window.localStorage?.getItem("useFunctionsEmulator") === "true");

    if (shouldUseFunctionsEmulator) {
      return `http://localhost:5001/${this.projectId}/${FUNCTIONS_REGION}/getServerTime`;
    }

    return `https://${FUNCTIONS_REGION}-${this.projectId}.cloudfunctions.net/getServerTime`;
  }

  async fetchTimeFromServerEndpoint() {
    const url = this.buildServerTimeUrl();
    if (!url) {
      console.warn(
        "⚠️ Server time endpoint URL unavailable (missing projectId)",
      );
      return null;
    }

    const hasPerformanceNow =
      typeof performance !== "undefined" &&
      typeof performance.now === "function";

    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      TIME_ENDPOINT_TIMEOUT_MS,
    );

    try {
      const beforeFetch = hasPerformanceNow ? performance.now() : Date.now();
      const response = await fetch(url, {
        method: "GET",
        mode: "cors",
        cache: "no-store",
        signal: controller.signal,
      });
      const afterFetch = hasPerformanceNow ? performance.now() : Date.now();

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const json = await response.json();
      const serverTimeMs = Number(json?.serverTimeMs);

      if (!Number.isFinite(serverTimeMs)) {
        throw new Error("Invalid serverTimeMs");
      }

      const networkLatency = (afterFetch - beforeFetch) / 2;
      return {
        source: "ServerTime",
        time: serverTimeMs + networkLatency,
        latency: networkLatency,
      };
    } catch (error) {
      const errorMsg = error.name === "AbortError" ? "Timeout" : error.message;
      console.warn(`⚠️ Server time endpoint failed (${url}) - ${errorMsg}`);
      return null;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Main sync method - fetches time from a first-party Cloud Function.
   * This is the authoritative time source for the app.
   */
  async syncNetworkTime() {
    if (!this.useAuthoritativeServerTime) {
      return;
    }

    if (this.syncing) {
      console.log("⏭️ Sync already in progress, skipping.");
      return;
    }

    if (this.isOffline) {
      console.log("⏭️ Device is offline, skipping sync.");
      return;
    }

    this.syncing = true;
    this.syncAttempts++;

    try {
      const serverResult = await this.fetchTimeFromServerEndpoint();
      if (serverResult) {
        const localTime = Date.now();
        const drift = serverResult.time - localTime;

        runInAction(() => {
          this.syncedServerTimeMs = serverResult.time;
          this.syncedDateNowMs = localTime; // Store wall-clock time at sync (survives sleep/hibernate)
          this.lastSyncSuccess = true;
          this.lastSyncError = null;
        });

        console.log(
          `✅ Time synced successfully (first-party endpoint):\n` +
            `   Network: ${new Date(serverResult.time).toISOString()}\n` +
            `   Local:   ${new Date(localTime).toISOString()}\n` +
            `   Drift:   ${drift > 0 ? "+" : ""}${drift}ms`,
        );
        return;
      }
      throw new Error("Server time endpoint unavailable");
    } catch (error) {
      console.error("❌ Time sync failed:", error.message);

      runInAction(() => {
        this.lastSyncSuccess = false;
        this.lastSyncError = error.message;
      });

      // If this is the first sync attempt and it failed, retry after a delay
      if (this.syncAttempts === 1 && this.syncedServerTimeMs == null) {
        console.log("↻ Retrying initial sync in 3 seconds...");
        setTimeout(() => this.syncNetworkTime(), 3000);
      }
    } finally {
      this.syncing = false;
    }
  }

  shouldResyncOnVisible() {
    // If we have never synced successfully, resync ASAP when visible.
    if (this.syncedServerTimeMs == null || !this.lastSyncSuccess) {
      return true;
    }

    // Check sync age using wall-clock time (Date.now survives sleep/hibernate)
    if (this.syncedDateNowMs != null) {
      const ageMs = Date.now() - this.syncedDateNowMs;

      // Negative age = clock went backwards, resync
      if (ageMs < 0) {
        return true;
      }

      // Sync too old, resync
      if (ageMs >= MAX_TRUSTED_SYNC_AGE_MS) {
        return true;
      }
    }

    return false;
  }

  setupVisibilityChangeListener() {
    if (this.visibilityChangeListener) {
      return;
    }

    const handleVisibilityChange = () => {
      if (typeof document === "undefined") {
        return;
      }

      if (document.visibilityState !== "visible") {
        return;
      }

      // Verify connectivity when tab becomes visible (only if offline)
      // Chrome's offline detection can be affected by tab visibility
      // Use debounced check to avoid excessive requests
      if (this.isOffline) {
        const timeSinceLastCheck = this.lastConnectivityCheck
          ? Date.now() - this.lastConnectivityCheck
          : Infinity;

        // Only check if it's been a while since last check
        if (timeSinceLastCheck > CONNECTIVITY_CHECK_MIN_INTERVAL_MS) {
          console.log(
            "🔄 NetworkManager: Tab visible while offline - verifying connectivity",
          );
          this.verifyConnectivity();
        }
      }

      if (this.isOffline || this.syncing) {
        return;
      }

      // Allow runtime toggling of authoritative time via Remote Config.
      this.refreshRemoteConfigForTime();

      if (this.shouldResyncOnVisible()) {
        console.log(
          "🔄 NetworkManager: Tab visible; last sync stale -> resync triggered",
        );
        this.syncNetworkTime();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    this.visibilityChangeListener = () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }

  /**
   * Returns a UTC‐corrected Date, or falls back to local Date if sync failed.
   * This is the main method other code should use to get the current time.
   *
   * Uses Date.now() for elapsed time calculation, which correctly handles
   * sleep/hibernate scenarios (unlike performance.now() which may pause).
   */
  getNetworkTime() {
    if (!this.useAuthoritativeServerTime) {
      return new Date();
    }

    if (this.syncedServerTimeMs != null && this.syncedDateNowMs != null) {
      const elapsedMs = Date.now() - this.syncedDateNowMs;

      // Sanity check: clock went backwards (user changed system time)
      if (elapsedMs < 0) {
        console.warn(
          `⚠️ NetworkManager: Negative elapsed time detected (${elapsedMs}ms). ` +
            `System clock may have changed. Triggering resync.`,
        );
        this.syncNetworkTime(); // Async resync
        return new Date(); // Fallback to local time
      }

      return new Date(this.syncedServerTimeMs + elapsedMs);
    }

    return new Date();
  }

  /**
   * Returns whether we have a reliable synced time.
   * Useful for features that absolutely require accurate time.
   */
  hasReliableTime() {
    return (
      this.useAuthoritativeServerTime &&
      this.syncedServerTimeMs != null &&
      this.lastSyncSuccess
    );
  }

  /**
   * Force a time sync (useful for debugging or after detecting issues)
   */
  async forceSync() {
    this.syncing = false; // Reset lock
    return this.syncNetworkTime();
  }

  async queueFirestoreNetworkChange(shouldEnable, reason) {
    this.pendingFirestoreNetworkEnabled = shouldEnable;
    this.pendingFirestoreNetworkReason = reason;

    if (!this.firestoreNetworkChangePromise) {
      this.firestoreNetworkChangePromise = this.processFirestoreNetworkQueue();
    }

    return this.firestoreNetworkChangePromise ?? Promise.resolve();
  }

  computeFirestoreToggleRetryDelayMs() {
    const exponent = Math.max(0, this.firestoreToggleConsecutiveFailures - 1);
    const scaledDelayMs =
      FIRESTORE_NETWORK_TOGGLE_MIN_INTERVAL_MS *
      2 ** Math.min(exponent, 10);
    return Math.min(scaledDelayMs, FIRESTORE_TOGGLE_RETRY_MAX_BACKOFF_MS);
  }

  logFirestoreToggleError(targetState, reason, error, retryDelayMs, errorType) {
    const now = Date.now();
    const shouldLog =
      !this.firestoreToggleErrorLastLoggedAt ||
      now - this.firestoreToggleErrorLastLoggedAt >=
        FIRESTORE_TOGGLE_ERROR_LOG_WINDOW_MS;

    if (!shouldLog) {
      this.firestoreToggleErrorSuppressedCount += 1;
      return;
    }

    this.firestoreToggleErrorLastLoggedAt = now;
    this.firestoreToggleErrorSuppressedCount = 0;

    const errorMessage =
      typeof error?.message === "string"
        ? error.message
        : "Unknown Firestore toggle error";
    const action = targetState ? "enable" : "disable";
    const retryDelaySeconds = Math.ceil(retryDelayMs / 1000);
    console.warn(
      `⚠️ Firestore ${action} failed (${errorType}, ${reason}). ${errorMessage}. Retrying with backoff (~${retryDelaySeconds}s).`,
    );
  }

  resetFirestoreToggleFailureState() {
    this.firestoreToggleConsecutiveFailures = 0;
    this.firestoreToggleBackoffUntil = null;
  }

  scheduleFirestoreNetworkReconciliation(reason, delayMs = 250) {
    this.pendingFirestoreReconciliationReason = reason || "unspecified";

    let effectiveDelayMs = Math.max(0, delayMs);
    if (this.firestoreToggleBackoffUntil) {
      const remainingBackoffMs = this.firestoreToggleBackoffUntil - Date.now();
      if (remainingBackoffMs > 0) {
        effectiveDelayMs = Math.max(effectiveDelayMs, remainingBackoffMs);
      }
    }

    if (this.firestoreNetworkReconciliationTimeoutId) {
      clearTimeout(this.firestoreNetworkReconciliationTimeoutId);
    }

    this.firestoreNetworkReconciliationTimeoutId = setTimeout(() => {
      this.firestoreNetworkReconciliationTimeoutId = null;
      const reconciliationReason =
        this.pendingFirestoreReconciliationReason || "unspecified";
      this.pendingFirestoreReconciliationReason = null;
      this.reconcileFirestoreNetworkState(reconciliationReason);
    }, effectiveDelayMs);
  }

  async reconcileFirestoreNetworkState(reason = "unspecified") {
    if (this.networkStateChangeInProgress) {
      this.scheduleFirestoreNetworkReconciliation(`${reason}-in-progress`);
      return;
    }

    const shouldEnable = !this.isOffline;
    if (
      this.firestoreNetworkEnabled !== null &&
      this.firestoreNetworkEnabled === shouldEnable
    ) {
      return;
    }

    await this.queueFirestoreNetworkChange(
      shouldEnable,
      `reconciliation:${reason}`,
    );
  }

  async processFirestoreNetworkQueue() {
    try {
      while (this.pendingFirestoreNetworkEnabled !== null) {
        const targetState = this.pendingFirestoreNetworkEnabled;
        const reason = this.pendingFirestoreNetworkReason || "unspecified";
        this.pendingFirestoreNetworkEnabled = null;
        this.pendingFirestoreNetworkReason = null;

        if (this.firestoreNetworkEnabled === targetState) {
          console.log(
            `✅ Firestore network already ${targetState ? "enabled" : "disabled"} (${reason})`,
          );
          continue;
        }

        const now = Date.now();
        const timeSinceLastChange = this.lastFirestoreNetworkChangeAt
          ? now - this.lastFirestoreNetworkChangeAt
          : Infinity;

        if (timeSinceLastChange < FIRESTORE_NETWORK_TOGGLE_MIN_INTERVAL_MS) {
          const waitMs =
            FIRESTORE_NETWORK_TOGGLE_MIN_INTERVAL_MS - timeSinceLastChange;
          await new Promise((resolve) => setTimeout(resolve, waitMs));

          if (
            this.pendingFirestoreNetworkEnabled !== null &&
            this.pendingFirestoreNetworkEnabled !== targetState
          ) {
            continue;
          }
        }

        this.networkStateChangeInProgress = true;
        try {
          if (targetState) {
            await enableNetwork(db);
          } else {
            await disableNetwork(db);
          }
          this.resetFirestoreToggleFailureState();
          this.firestoreNetworkEnabled = targetState;
          this.lastFirestoreNetworkChangeAt = Date.now();
          console.log(
            `✅ Firestore network ${targetState ? "enabled" : "disabled"} (${reason})`,
          );
        } catch (error) {
          const isUnexpectedStateError =
            typeof error?.message === "string" &&
            error.message.includes("Unexpected state");
          this.firestoreToggleConsecutiveFailures += 1;
          const retryDelayMs = this.computeFirestoreToggleRetryDelayMs();
          this.firestoreToggleBackoffUntil = Date.now() + retryDelayMs;
          this.logFirestoreToggleError(
            targetState,
            reason,
            error,
            retryDelayMs,
            isUnexpectedStateError ? "unexpected-state" : "toggle-error",
          );

          this.firestoreNetworkEnabled = null;
          this.lastFirestoreNetworkChangeAt = Date.now();
          this.scheduleFirestoreNetworkReconciliation(
            "toggle-error",
            retryDelayMs,
          );
        } finally {
          this.networkStateChangeInProgress = false;
        }
      }
    } finally {
      this.firestoreNetworkChangePromise = null;
      if (this.pendingFirestoreNetworkEnabled !== null) {
        this.firestoreNetworkChangePromise =
          this.processFirestoreNetworkQueue();
      }
    }
  }

  /* ONLINE/OFFLINE LISTENER */
  setupConnectionListener(coach, t) {
    if (this.connectionListener) {
      console.log("Removing old sync status listener...");
      this.removeConnectionListener();
    }

    console.log("Setting up sync status listener...");

    const handleOnline = async () => {
      const eventVersion = ++this.connectivityEventVersion;
      const timerLabel = `handleOnline Time #${eventVersion}`;

      if (this.networkStateChangeInProgress) {
        console.log(
          "⚠️ Network state change already in progress, continuing with verification",
        );
      }

      console.time(timerLabel);
      console.log("handleOnline event triggered");

      console.log("🟢 Browser online event triggered - verifying connectivity");

      // PRIMARY APPROACH: Trust Chrome's online event, but verify with network request
      // This catches Chrome's false online detection while relying on browser events
      // Use a short delay to allow network stack to stabilize after Wi-Fi reconnection
      await new Promise((resolve) =>
        setTimeout(resolve, CONNECTIVITY_VERIFY_DELAY_MS),
      );

      if (eventVersion !== this.connectivityEventVersion) {
        console.log("⏭️ Skipping stale online handler after delay");
        console.timeEnd(timerLabel);
        return;
      }

      // Double-check guard after delay - another handler might have started
      if (this.networkStateChangeInProgress) {
        console.log(
          "⚠️ Network state change started during delay, continuing with verification",
        );
      }

      // Verify actual connectivity - don't use cache, always check
      const actuallyOnline = await this.checkRealConnectivity({
        allowSameOrigin: false,
      });

      if (eventVersion !== this.connectivityEventVersion) {
        console.log(
          "⏭️ Skipping stale online handler after connectivity check",
        );
        console.timeEnd(timerLabel);
        return;
      }

      if (!actuallyOnline) {
        // Chrome incorrectly reported online - fix the state
        console.log(
          "⚠️ Chrome reported online but connectivity check failed - fixing state",
        );
        runInAction(() => {
          this.isOffline = true;
          this.connectivityVerified = false;
        });
        // Update cache
        this.lastConnectivityResult = false;
        this.lastConnectivityResultTime = Date.now();
        await this.queueFirestoreNetworkChange(false, "handleOnline-mismatch");
        // Reschedule periodic connectivity checks with offline interval (60 seconds)
        this.stopConnectivityCheckInterval();
        this.setupConnectivityCheckInterval();
        console.timeEnd(timerLabel);
        return;
      }

      const wasOffline = this.isOffline;

      // Chrome was correct - we're actually online
      console.log(
        "✅ Connectivity verified online - proceeding with online handler",
      );

      runInAction(() => {
        this.isOffline = false;
        this.connectivityVerified = true;
      });

      // Update cache
      this.lastConnectivityResult = true;
      this.lastConnectivityResultTime = Date.now();

      await this.queueFirestoreNetworkChange(true, "handleOnline");

      if (eventVersion !== this.connectivityEventVersion) {
        console.log(
          "⏭️ Skipping stale online handler after Firestore queue processing",
        );
        console.timeEnd(timerLabel);
        return;
      }

      if (wasOffline) {
        // Re-sync time when coming back online
        this.syncNetworkTime();

        // Show online toast notification
        if (coach.showToast) {
          coach.showToast("connectionRestored-string", {
            type: "info",
            description: "connectionRestoredDescription-string",
            duration: 7000,
          });
        }

        if (coach.syncStatusManager.hasChangesOffline) {
          await coach.syncStatusManager.safeOperation(
            "performTransaction",
            () => coach.performTransaction(),
          );
          if (
            coach.syncStatusManager.operations.performTransaction ===
            SyncStatus.SYNCED
          ) {
            coach.syncStatusManager.hasChangesOffline = false;
          }
        }

        const syncClientsInParallel = async (
          clientsWithChangesOffline,
          batchSize,
        ) => {
          for (
            let i = 0;
            i < clientsWithChangesOffline.length;
            i += batchSize
          ) {
            const batch = clientsWithChangesOffline.slice(i, i + batchSize); // Select a batch of clients
            await Promise.all(
              batch.map(async (client) => {
                // client.syncStatusManager.clearOfflineOperations();
                await client.syncStatusManager.safeOperation(
                  "performTransaction",
                  () => client.performTransaction(),
                );

                if (
                  client.syncStatusManager.operations.performTransaction ===
                  SyncStatus.SYNCED
                ) {
                  client.syncStatusManager.setProperty(
                    "hasChangesOffline",
                    false,
                  );
                }
              }),
            );
          }
        };

        const clientsWithChangesOffline = coach.clients.filter(
          (c) => c.syncStatusManager.hasChangesOffline,
        );
        if (clientsWithChangesOffline.length > 0) {
          await syncClientsInParallel(clientsWithChangesOffline, 4);
        }

        await coach.syncStatusManager.resolveErrors();

        if (coach.selectedClientId && coach.selectedClientId != "") {
          const selectedClient = coach.clients.find(
            (c) => c.id === coach.selectedClientId,
          );
          selectedClient?.firestoreListener?.setupBlocksListener();
        }
      }

      // Reschedule periodic connectivity checks with online interval (5 minutes)
      // This is a fallback in case browser events don't fire - browser events are primary
      this.stopConnectivityCheckInterval();
      this.setupConnectivityCheckInterval();

      console.timeEnd(timerLabel);
    };

    const handleOffline = async () => {
      const eventVersion = ++this.connectivityEventVersion;

      if (this.networkStateChangeInProgress) {
        console.log(
          "⚠️ Network state change already in progress, continuing with verification",
        );
      }

      console.log(
        "🔴 Browser offline event triggered - verifying connectivity",
      );

      // PRIMARY APPROACH: Trust Chrome's offline event, but verify with network request
      // This catches Chrome's false offline detection while relying on browser events
      // Use a short delay to allow network stack to stabilize after Wi-Fi disconnection
      await new Promise((resolve) =>
        setTimeout(resolve, CONNECTIVITY_VERIFY_DELAY_MS),
      );

      if (eventVersion !== this.connectivityEventVersion) {
        console.log("⏭️ Skipping stale offline handler after delay");
        return;
      }

      // Double-check guard after delay - another handler might have started
      if (this.networkStateChangeInProgress) {
        console.log(
          "⚠️ Network state change started during delay, continuing with verification",
        );
      }

      const actuallyOnline = await this.checkRealConnectivity({
        allowSameOrigin: false,
      });

      if (eventVersion !== this.connectivityEventVersion) {
        console.log(
          "⏭️ Skipping stale offline handler after connectivity check",
        );
        return;
      }

      if (actuallyOnline) {
        // Chrome incorrectly reported offline - fix the state
        console.log(
          "✅ Chrome reported offline but connectivity check shows online - fixing state",
        );
        runInAction(() => {
          this.isOffline = false;
          this.connectivityVerified = true;
        });
        // Update cache
        this.lastConnectivityResult = true;
        this.lastConnectivityResultTime = Date.now();
        await this.queueFirestoreNetworkChange(true, "handleOffline-mismatch");
        // Reschedule periodic connectivity checks with online interval (5 minutes)
        this.stopConnectivityCheckInterval();
        this.setupConnectivityCheckInterval();
        return;
      }

      const wasOffline = this.isOffline;

      // Chrome was correct - we're actually offline
      console.log(
        "⚠️ Connectivity verified offline - proceeding with offline handler",
      );

      runInAction(() => {
        this.isOffline = true;
        this.connectivityVerified = false;
      });

      // Update cache
      this.lastConnectivityResult = false;
      this.lastConnectivityResultTime = Date.now();

      await this.queueFirestoreNetworkChange(false, "handleOffline");

      if (eventVersion !== this.connectivityEventVersion) {
        console.log(
          "⏭️ Skipping stale offline handler after Firestore queue processing",
        );
        return;
      }

      // Reschedule periodic connectivity checks with offline interval (60 seconds)
      // This is a fallback in case browser events don't fire
      this.stopConnectivityCheckInterval();
      this.setupConnectivityCheckInterval();

      if (!wasOffline) {
        // Show offline toast notification
        if (coach.showToast) {
          coach.showToast("connectionLost-string", {
            type: "info",
            description: "connectionLostDescription-string",
            duration: 10000,
          });
        }

        const clientsWithBlocksListener = coach.clients.filter(
          (c) => c.syncStatusManager?.operations?.listenerBlocks != null,
        );
        clientsWithBlocksListener.forEach((c) =>
          c.firestoreListener.removeBlocksListener(),
        );
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Set up periodic connectivity checks as FALLBACK only
    // PRIMARY mechanism is browser online/offline events
    // This fallback runs infrequently (5min online, 60s offline) to catch edge cases
    this.setupConnectivityCheckInterval();

    this.connectionListener = () => {
      // Invalidate any in-flight async event handlers tied to this listener instance.
      this.connectivityEventVersion++;
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      this.stopConnectivityCheckInterval();
    };
  }

  removeConnectionListener() {
    if (this.connectionListener) {
      console.log("Removing sync status listener...");
      this.connectionListener();
      this.connectionListener = null;
    } else {
      console.log("No active sync status listener to remove.");
    }
    this.stopConnectivityCheckInterval();
  }

  /**
   * Verifies actual internet connectivity by making a real network request.
   * This is more reliable than navigator.onLine which can incorrectly report offline
   * even when internet is available (common Chrome bug).
   *
   * Uses lightweight HEAD requests and implements debouncing/caching to minimize
   * network overhead and prevent excessive checks.
   */
  async verifyConnectivity(force = false) {
    // Prevent concurrent checks
    if (this.connectivityCheckInProgress) {
      console.log(
        "⏭️ Connectivity check already in progress, skipping duplicate request",
      );
      return;
    }

    // When force=true, ALWAYS verify and update isOffline
    // This is critical for fixing Chrome's incorrect offline detection
    if (force) {
      this.connectivityCheckInProgress = true;
      this.lastConnectivityCheck = Date.now();

      try {
        const actuallyOnline = await this.checkRealConnectivity({
          allowSameOrigin: false,
        });
        const wasOffline = this.isOffline;
        const wasConnectivityVerified = this.connectivityVerified;

        // CRITICAL: Update isOffline IMMEDIATELY and SYNCHRONOUSLY
        // This ensures UI elements that check isOffline get the correct value
        // NOTE: We do NOT dispatch events here to avoid triggering handleOnline/handleOffline
        // which would call enableNetwork/disableNetwork and cause race conditions.
        // Events should only be dispatched by actual browser online/offline events.
        runInAction(() => {
          this.connectivityVerified = actuallyOnline;
          this.isOffline = !actuallyOnline; // Fix isOffline state
          this.lastConnectivityResult = actuallyOnline;
          this.lastConnectivityResultTime = Date.now();
        });

        if (actuallyOnline) {
          console.log(
            "✅ Connectivity verified: Actually online, fixing isOffline to false",
          );
        } else {
          console.log(
            "⚠️ Connectivity verified: Actually offline, setting isOffline to true",
          );
        }

        const stateChanged =
          wasOffline !== this.isOffline ||
          wasConnectivityVerified !== this.connectivityVerified;
        const shouldReconcileInitialOffline =
          this.lastFirestoreNetworkChangeAt === null &&
          this.firestoreNetworkEnabled === true &&
          this.isOffline;
        if (
          stateChanged ||
          this.firestoreNetworkEnabled === null ||
          shouldReconcileInitialOffline
        ) {
          this.scheduleFirestoreNetworkReconciliation("verifyConnectivity-force");
        }
      } catch (error) {
        console.warn("⚠️ Connectivity check error:", error);
        // On error, don't change state - keep current state
      } finally {
        this.connectivityCheckInProgress = false;
      }
      return;
    }

    // Debouncing: Don't check too frequently
    const timeSinceLastCheck = this.lastConnectivityCheck
      ? Date.now() - this.lastConnectivityCheck
      : Infinity;

    if (timeSinceLastCheck < CONNECTIVITY_CHECK_MIN_INTERVAL_MS) {
      return;
    }

    // Don't use cache for periodic checks - always verify actual connectivity
    // This ensures we detect Wi-Fi removal even if browser doesn't fire offline event
    // Cache is only used in handleOnline/handleOffline to avoid redundant checks during events

    this.connectivityCheckInProgress = true;
    this.lastConnectivityCheck = Date.now();

    try {
      // Always verify with actual network request to detect runtime changes
      // This ensures connectivityVerified is updated even when navigator.onLine matches isOffline
      const actuallyOnline = await this.checkRealConnectivity({
        allowSameOrigin: false,
      });

      // Cache the result
      this.lastConnectivityResult = actuallyOnline;
      this.lastConnectivityResultTime = Date.now();

      // Store previous state to detect changes (before updating)
      const wasOffline = this.isOffline;
      const wasConnectivityVerified = this.connectivityVerified;

      // ALWAYS update connectivityVerified and isOffline based on actual connectivity
      // This is critical for detecting runtime connectivity changes
      runInAction(() => {
        this.connectivityVerified = actuallyOnline;
        this.isOffline = !actuallyOnline;
      });

      // Detect and log state changes
      // FALLBACK ONLY: Only dispatch events if state changed AND no network state change is in progress
      // This prevents race conditions with Firestore enableNetwork/disableNetwork
      // NOTE: This is a fallback mechanism - browser events are primary
      if (actuallyOnline && wasOffline) {
        // We were offline but are now online (fallback detected change)
        console.log(
          "✅ Fallback check: Actually online - updating isOffline to false",
        );
        // Only trigger event if no network state change is in progress
        // Use setTimeout to ensure we're not in the middle of another operation
        if (!this.networkStateChangeInProgress) {
          // Small delay to ensure any pending operations complete
          // Capture state at dispatch time to prevent stale state in event handlers
          setTimeout(() => {
            // Re-check state at dispatch time to ensure we're dispatching with current state
            const currentIsOffline = this.isOffline;
            const currentConnectivityVerified = this.connectivityVerified;
            if (
              !this.networkStateChangeInProgress &&
              currentConnectivityVerified &&
              !currentIsOffline
            ) {
              window.dispatchEvent(new Event("online"));
            } else {
              console.log(
                "⚠️ Skipping online event dispatch - network state change started or state changed",
              );
              this.scheduleFirestoreNetworkReconciliation(
                "fallback-online-suppressed",
              );
            }
          }, 100);
        } else {
          console.log(
            "⚠️ Skipping online event dispatch - network state change in progress",
          );
          this.scheduleFirestoreNetworkReconciliation(
            "fallback-online-suppressed",
          );
        }
      } else if (!actuallyOnline && !wasOffline) {
        // We were online but are now offline (fallback detected connectivity loss)
        console.log(
          "⚠️ Fallback check: Actually offline - updating isOffline to true",
        );
        // Only trigger event if no network state change is in progress
        // Use setTimeout to ensure we're not in the middle of another operation
        if (!this.networkStateChangeInProgress) {
          // Small delay to ensure any pending operations complete
          // Capture state at dispatch time to prevent stale state in event handlers
          setTimeout(() => {
            // Re-check state at dispatch time to ensure we're dispatching with current state
            const currentIsOffline = this.isOffline;
            const currentConnectivityVerified = this.connectivityVerified;
            if (
              !this.networkStateChangeInProgress &&
              !currentConnectivityVerified &&
              currentIsOffline
            ) {
              window.dispatchEvent(new Event("offline"));
            } else {
              console.log(
                "⚠️ Skipping offline event dispatch - network state change started or state changed",
              );
              this.scheduleFirestoreNetworkReconciliation(
                "fallback-offline-suppressed",
              );
            }
          }, 100);
        } else {
          console.log(
            "⚠️ Skipping offline event dispatch - network state change in progress",
          );
          this.scheduleFirestoreNetworkReconciliation(
            "fallback-offline-suppressed",
          );
        }
      } else if (actuallyOnline !== wasConnectivityVerified) {
        // connectivityVerified changed (fallback detected change)
        console.log(
          `🔄 Fallback check: connectivityVerified changed from ${wasConnectivityVerified} to ${actuallyOnline}`,
        );
      }
    } catch (error) {
      console.warn("⚠️ Connectivity check error:", error);
      // On error, don't change state - keep current state
    } finally {
      this.connectivityCheckInProgress = false;
    }
  }

  /**
   * Performs actual network requests to verify real internet connectivity.
   * Uses lightweight HEAD requests and stops at first success for efficiency.
   * Implements best practices: minimal bandwidth, fast timeout, single endpoint success.
   */
  async checkRealConnectivity(options = {}) {
    const { allowSameOrigin = true } = options;

    // List of endpoints to try (in order of preference)
    // Prefer external endpoints to avoid service worker cache false positives
    const endpoints = [
      // External connectivity checks (no-cors, reliable)
      {
        url: "https://www.gstatic.com/generate_204",
        method: "GET",
        mode: "no-cors",
      },
      {
        url: "https://clients3.google.com/generate_204",
        method: "GET",
        mode: "no-cors",
      },
      // Try Firebase project (if available) - lightweight check
      ...(this.projectId
        ? [
            {
              url: `https://${this.projectId}.firebaseapp.com`,
              method: "HEAD",
              mode: "no-cors",
            },
          ]
        : []),
      // Same-origin is optional (can be intercepted by service worker)
      ...(allowSameOrigin
        ? [
            {
              url: window.location.origin,
              method: "HEAD",
              mode: "same-origin",
            },
          ]
        : []),
    ];

    // Try endpoints sequentially, stop on first success (lightweight approach)
    for (const endpoint of endpoints) {
      try {
        const controller = new AbortController();
        let timeoutId = null;
        try {
          timeoutId = setTimeout(
            () => controller.abort(),
            CONNECTIVITY_CHECK_TIMEOUT_MS,
          );

          // Add cache-busting query parameter to prevent cached responses
          // Use timestamp for uniqueness
          let url;
          try {
            url = new URL(endpoint.url);
            url.searchParams.append("_t", Date.now().toString());
          } catch (urlError) {
            // URL construction failed (e.g., invalid endpoint.url or window.location.origin)
            console.warn(
              `⚠️ Invalid URL for connectivity check: ${endpoint.url}`,
              urlError,
            );
            continue; // Skip this endpoint and try the next one
          }

          const response = await fetch(url.toString(), {
            method: endpoint.method,
            mode: endpoint.mode,
            cache: "no-store", // Prevent caching
            headers: {
              "Cache-Control": "no-store",
              Pragma: "no-cache",
            },
            signal: controller.signal,
            // Additional lightweight options
            keepalive: false, // Don't keep connection alive
          });

          // For same-origin requests, check response status
          if (endpoint.mode === "same-origin") {
            if (response.ok || response.status < 500) {
              // Any non-server-error response indicates connectivity
              // Stop here - no need to try other endpoints
              return true;
            }
          } else {
            // For no-cors requests, if we got here without an error, we have connectivity
            // (no-cors mode means we can't check response.ok, but no error = success)
            // Stop here - no need to try other endpoints
            return true;
          }
        } finally {
          if (timeoutId !== null) {
            clearTimeout(timeoutId);
          }
        }
      } catch (error) {
        // Continue to next endpoint if this one fails
        // Timeout or network errors mean this endpoint is unreachable
        // Don't log here to avoid console spam
        continue;
      }
    }

    // All endpoints failed - we're offline
    return false;
  }

  /**
   * Sets up periodic connectivity checks as a FALLBACK mechanism.
   * PRIMARY mechanism is browser online/offline events.
   * This fallback only runs infrequently to catch edge cases where browser events don't fire.
   *
   * Optimized: Uses different intervals based on current state to minimize network requests.
   */
  setupConnectivityCheckInterval() {
    // Stop existing interval if any
    this.stopConnectivityCheckInterval();

    const performCheck = () => {
      // FALLBACK ONLY: Periodic check to catch edge cases
      // Primary mechanism is browser events (online/offline)
      // This only runs infrequently as a safety net
      this.verifyConnectivity();

      // Reschedule with appropriate interval based on current state
      // More frequent when offline (to detect restoration), less frequent when online
      const interval = this.isOffline
        ? CONNECTIVITY_CHECK_INTERVAL_OFFLINE_MS // Check every 60s when offline (fallback)
        : CONNECTIVITY_CHECK_INTERVAL_ONLINE_MS; // Check every 5min when online (fallback)

      this.connectivityCheckIntervalId = setTimeout(() => {
        this.connectivityCheckIntervalId = null;
        performCheck();
      }, interval);
    };

    // Start with appropriate interval based on current state
    const initialInterval = this.isOffline
      ? CONNECTIVITY_CHECK_INTERVAL_OFFLINE_MS
      : CONNECTIVITY_CHECK_INTERVAL_ONLINE_MS;

    this.connectivityCheckIntervalId = setTimeout(() => {
      this.connectivityCheckIntervalId = null;
      performCheck();
    }, initialInterval);
  }

  /**
   * Stops the periodic connectivity check interval.
   */
  stopConnectivityCheckInterval() {
    if (this.connectivityCheckIntervalId) {
      // Handle both setInterval and setTimeout (for dynamic intervals)
      if (typeof this.connectivityCheckIntervalId === "number") {
        clearTimeout(this.connectivityCheckIntervalId);
      } else {
        clearInterval(this.connectivityCheckIntervalId);
      }
      this.connectivityCheckIntervalId = null;
    }
  }

  /**
   * Clears the connectivity result cache.
   * Useful when you want to force a fresh check.
   */
  clearConnectivityCache() {
    this.lastConnectivityResult = null;
    this.lastConnectivityResultTime = null;
  }

  /**
   * Ensures connectivity is verified before proceeding.
   * Blocks until connectivity is confirmed or timeout.
   * CRITICAL: This fixes isOffline state before any UI checks it.
   */
  async ensureConnectivityVerified(maxWaitMs = 5000) {
    // If already verified and online, return immediately
    if (this.connectivityVerified && !this.isOffline) {
      return true;
    }

    const waitBudgetMs = Math.max(0, maxWaitMs);
    const start = Date.now();

    while (Date.now() - start <= waitBudgetMs) {
      // If no check is in progress, force one now.
      if (!this.connectivityCheckInProgress) {
        await this.verifyConnectivity(true);
      }

      if (this.connectivityVerified && !this.isOffline) {
        return true;
      }

      // If we are not currently checking and still offline, no need to wait further.
      if (!this.connectivityCheckInProgress && this.isOffline) {
        return false;
      }

      const elapsedMs = Date.now() - start;
      const remainingMs = waitBudgetMs - elapsedMs;
      if (remainingMs <= 0) {
        break;
      }

      // Wait briefly for any in-flight check to complete.
      await new Promise((resolve) =>
        setTimeout(resolve, Math.min(100, remainingMs)),
      );
    }

    // Fallback final state after timeout budget is exhausted.
    return this.connectivityVerified && !this.isOffline;
  }

  /* BEFORE UNLOAD LISTENER */
  setupBeforeUnloadListener(coach) {
    if (this.beforeUnloadListener) {
      console.log("Removing old before unload listener...");
      this.removeBeforeUnloadListener();
    }

    console.log("Setting up before unload listener...");

    const handleBeforeUnload = (event) => {
      if (coach.isOffline || coach.changesMadeOffline) {
        const message =
          "You have unsaved changes. Are you sure you want to leave?";
        event.returnValue = message;
        return message;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    this.beforeUnloadListener = () =>
      window.removeEventListener("beforeunload", handleBeforeUnload);
  }

  removeBeforeUnloadListener() {
    if (this.beforeUnloadListener) {
      console.log("Removing before unload listener...");
      this.beforeUnloadListener();
      this.beforeUnloadListener = null;
    } else {
      console.log("No active before unload listener to remove.");
    }
  }
}

export default NetworkManager;
